import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import {
  FaArrowLeft,
  FaSave,
  FaTimes,
  FaEdit,
  FaExclamationTriangle,
  FaRedo,
} from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../../services/authServices";

const STATUSES = [
  "Packaging",
  "Shipped",
  "In Transit",
  "Out for Delivery",
  "Delivered",
  "Delayed",
  "Cancelled",
];

const EditShipment = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [shipment, setShipment] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [formData, setFormData] = useState({
    status: "",
    location: "",
    description: "",
    isDelayed: false,
    delayReason: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");

  const fetchShipment = async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const { data } = await api.get(`/api/user/shipment/${id}`);
      const s = data.data || data;
      setShipment(s);
      setFormData({
        status: s.status || "Packaging",
        location: "",
        description: "",
        isDelayed: !!s.isDelayed,
        delayReason: s.delayReason || "",
      });
    } catch (err) {
      setLoadError(
        err?.response?.data?.message || "Failed to load shipment.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchShipment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setFormSuccess("");

    if (formData.isDelayed && !formData.delayReason.trim()) {
      setFormError("Please provide a delay reason when marking as delayed");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        status: formData.status,
        isDelayed: formData.isDelayed,
        delayReason: formData.isDelayed ? formData.delayReason : "",
      };
      if (formData.location.trim()) payload.location = formData.location;
      if (formData.description.trim())
        payload.description = formData.description;

      const { data } = await api.put(`/api/user/shipment/${id}`, payload);

      if (data.success) {
        setFormSuccess("Shipment updated successfully!");
        setTimeout(() => navigate("/admin-dashboard"), 1500);
      } else {
        setFormError(data.message || "Failed to update shipment");
      }
    } catch (err) {
      setFormError(
        err?.response?.data?.message ||
          err?.message ||
          "Something went wrong",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Edit Shipment | Admin Dashboard</title>
      </Helmet>

      <PageWrapper>
        <Container>
          <Header>
            <BackButton onClick={() => navigate("/admin-dashboard")}>
              <FaArrowLeft /> Back to Dashboard
            </BackButton>
            <TitleSection>
              <FaEdit />
              <div>
                <h1>Edit Shipment</h1>
                <p>
                  {shipment
                    ? `Tracking No: ${shipment.trackingNumber}`
                    : "Update status and delivery information"}
                </p>
              </div>
            </TitleSection>
          </Header>

          {isLoading ? (
            <StateCard>
              <div className="spinner" />
              <p>Loading shipment…</p>
            </StateCard>
          ) : loadError ? (
            <StateCard>
              <FaExclamationTriangle className="err-icon" />
              <p>{loadError}</p>
              <button onClick={fetchShipment}>
                <FaRedo /> Retry
              </button>
            </StateCard>
          ) : (
            <Form onSubmit={handleSubmit}>
              {shipment && (
                <SummaryBox>
                  <div>
                    <span className="label">Route</span>
                    <span className="value">
                      {shipment.originAddress?.city} →{" "}
                      {shipment.destinationAddress?.city}
                    </span>
                  </div>
                  <div>
                    <span className="label">Sender</span>
                    <span className="value">{shipment.sender?.name}</span>
                  </div>
                  <div>
                    <span className="label">Receiver</span>
                    <span className="value">{shipment.receiver?.name}</span>
                  </div>
                  <div>
                    <span className="label">Current Status</span>
                    <span className="value">{shipment.status}</span>
                  </div>
                </SummaryBox>
              )}

              <FormSection>
                <SectionTitle>Status Update</SectionTitle>
                <TwoColumnGrid>
                  <FormGroup>
                    <label>Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) =>
                        setFormData((p) => ({ ...p, status: e.target.value }))
                      }
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </FormGroup>

                  <FormGroup>
                    <label>Current Location (optional)</label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) =>
                        setFormData((p) => ({
                          ...p,
                          location: e.target.value,
                        }))
                      }
                      placeholder="e.g. Chicago, IL"
                    />
                  </FormGroup>

                  <FormGroup $fullWidth>
                    <label>Update Note (optional)</label>
                    <textarea
                      rows="2"
                      value={formData.description}
                      onChange={(e) =>
                        setFormData((p) => ({
                          ...p,
                          description: e.target.value,
                        }))
                      }
                      placeholder="Shown in the customer's tracking history, e.g. Departed sorting facility"
                    />
                  </FormGroup>
                </TwoColumnGrid>
              </FormSection>

              <FormSection>
                <SectionTitle>Delay Information</SectionTitle>
                <CheckboxGroup>
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.isDelayed}
                      onChange={(e) =>
                        setFormData((p) => ({
                          ...p,
                          isDelayed: e.target.checked,
                        }))
                      }
                    />
                    <span>This shipment is delayed</span>
                  </label>
                </CheckboxGroup>

                {formData.isDelayed && (
                  <FormGroup>
                    <label>Delay Reason *</label>
                    <textarea
                      rows="2"
                      value={formData.delayReason}
                      onChange={(e) =>
                        setFormData((p) => ({
                          ...p,
                          delayReason: e.target.value,
                        }))
                      }
                      placeholder="e.g. Severe weather along the route"
                    />
                  </FormGroup>
                )}
              </FormSection>

              {formError && (
                <ErrorMessage>
                  <FaTimes />
                  <span>{formError}</span>
                </ErrorMessage>
              )}

              {formSuccess && (
                <SuccessMessage>
                  <span>✓</span>
                  <span>{formSuccess}</span>
                </SuccessMessage>
              )}

              <FormActions>
                <CancelButton
                  type="button"
                  onClick={() => navigate("/admin-dashboard")}
                >
                  Cancel
                </CancelButton>
                <SubmitButton type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Saving…" : "Save Changes"}
                  {!isSubmitting && <FaSave />}
                </SubmitButton>
              </FormActions>
            </Form>
          )}
        </Container>
      </PageWrapper>
    </>
  );
};

/* ─── Styles (mirrors CreateShipment.jsx) ─────────────────────────────────── */
const PageWrapper = styled.div`
  min-height: 100vh;
  background: #f0f2f5;
  padding: 2rem;

  @media (max-width: 600px) {
    padding: 1rem;
  }
`;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 2rem;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: #0b1e33;
  font-size: 0.9rem;
  cursor: pointer;
  margin-bottom: 1rem;
  padding: 0.5rem 0;

  &:hover {
    color: #d4af37;
  }

  svg {
    font-size: 0.8rem;
  }
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  svg {
    font-size: 2rem;
    color: #d4af37;
  }

  h1 {
    font-size: 1.75rem;
    font-weight: 700;
    color: #0b1e33;
    margin: 0 0 0.25rem;
  }

  p {
    font-size: 0.85rem;
    color: #8a9ab0;
    margin: 0;
  }

  @media (max-width: 768px) {
    svg {
      font-size: 1.5rem;
    }

    h1 {
      font-size: 1.3rem;
    }
  }
`;

const StateCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 3rem 2rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  text-align: center;

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #e2e8f0;
    border-top-color: #d4af37;
    border-radius: 50%;
    animation: spin 0.75s linear infinite;
  }

  .err-icon {
    font-size: 1.8rem;
    color: #e53e3e;
  }

  p {
    font-size: 0.88rem;
    color: #718096;
  }

  button {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.5rem 1.1rem;
    background: #d4af37;
    color: #fff;
    border: none;
    border-radius: 8px;
    font-size: 0.82rem;
    font-weight: 600;
    cursor: pointer;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const SummaryBox = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  background: #f8f9fa;
  border-radius: 12px;
  padding: 1rem 1.25rem;
  margin-bottom: 2rem;

  div {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
  }

  .label {
    font-size: 0.65rem;
    font-weight: 700;
    color: #a0aec0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .value {
    font-size: 0.85rem;
    font-weight: 600;
    color: #0b1e33;
  }

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Form = styled.form`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);

  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const FormSection = styled.div`
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #e2e8f0;

  &:last-of-type {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: #0b1e33;
  margin-bottom: 1.25rem;
`;

const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const FormGroup = styled.div`
  ${(p) => p.$fullWidth && "grid-column: 1 / -1;"}

  label {
    display: block;
    font-size: 0.75rem;
    font-weight: 600;
    color: #4a5568;
    margin-bottom: 0.5rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  input,
  select,
  textarea {
    width: 100%;
    padding: 0.6rem 0.75rem;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 0.85rem;
    font-family: inherit;
    transition: all 0.2s;

    &:focus {
      outline: none;
      border-color: #d4af37;
      box-shadow: 0 0 0 2px rgba(212, 175, 55, 0.1);
    }
  }

  textarea {
    resize: vertical;
  }
`;

const CheckboxGroup = styled.div`
  margin-bottom: 1rem;

  label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;

    input {
      width: 18px;
      height: 18px;
      cursor: pointer;
    }

    span {
      font-size: 0.85rem;
      color: #4a5568;
    }
  }
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  margin: 1rem 0;

  svg {
    color: #dc2626;
    font-size: 0.9rem;
    flex-shrink: 0;
  }

  span {
    font-size: 0.85rem;
    color: #dc2626;
  }
`;

const SuccessMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #dcfce7;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  margin: 1rem 0;

  span:first-child {
    color: #16a34a;
    font-size: 1rem;
    font-weight: bold;
  }

  span:last-child {
    font-size: 0.85rem;
    color: #16a34a;
  }
`;

const FormActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const CancelButton = styled.button`
  padding: 0.7rem 1.5rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
    border-color: #cbd5e0;
  }
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.7rem 1.5rem;
  background: #d4af37;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #c09b28;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

export default EditShipment;
